import type { UIThresholdModel } from '../catalog-transform';
import type { PlotSpec } from '../threshold-function-preview';
import { buildThresholdPlotSpec } from '../threshold-function-preview';
import type {
  ThresholdBundleFormValues,
  ThresholdSourceBindingItem,
} from './threshold-bundle-form.hook';

// -- type definitions --

export interface ThresholdFormulaPreviewInput {
  values: ThresholdBundleFormValues;
  sourceBindings: ThresholdSourceBindingItem[];
}

export interface ThresholdFormulaPreviewDescriptor {
  latexFormula: string;
  plotSpec: PlotSpec;
}

export type ThresholdFormulaPreviewProvider = (
  input: ThresholdFormulaPreviewInput
) => Promise<ThresholdFormulaPreviewDescriptor>;

// -- constants --

const NATIVE_THRESHOLD_TYPES = new Set([
  'constant',
  'affine',
  'polynomial',
  'piecewise',
  'circle',
  'rectangle',
  'mesh',
]);

// -- functions --

const toUiThresholdModel = (
  input: ThresholdFormulaPreviewInput
): UIThresholdModel => {
  const thresholdType = input.values.definition.threshold_type.trim();
  const normalizedType = NATIVE_THRESHOLD_TYPES.has(thresholdType)
    ? thresholdType
    : 'custom';

  return {
    id: -1,
    thresholdId: -1,
    label: input.values.default_style.label || input.values.definition.name,
    description: input.values.definition.description,
    boundKind: input.values.definition.bound_kind,
    typeLabel: thresholdType || 'custom',
    type: normalizedType as UIThresholdModel['type'],
    variables: [],
    variableDetails: [],
    parameterSummary: '',
    rawParameters: input.values.definition.parameters,
    isLegacy: false,
    evaluationCoordinateSystem:
      input.values.definition.evaluation_coordinate_system,
    representationCoordinateSystem:
      input.values.definition.representation_coordinate_system,
    representationMode: input.values.definition.representation_mode,
    activeFrom: input.values.definition.active_from || null,
    activeTo: input.values.definition.active_to || null,
    defaultStyle: {
      color: input.values.default_style.color,
      dash: null,
      width: input.values.default_style.width,
      fillColor: input.values.default_style.fill_color,
      opacity: null,
      rule: null,
    },
    availableStyles: [],
    sourceBindings: input.sourceBindings.map((binding) => ({
      measure_point_id: binding.measure_point_id,
      measure_type_id: binding.measure_type_id,
    })),
  };
};

export const nativeThresholdFormulaPreviewProvider: ThresholdFormulaPreviewProvider =
  async (input) => {
    const plotSpec = buildThresholdPlotSpec(toUiThresholdModel(input));
    return {
      latexFormula: plotSpec.formula,
      plotSpec,
    };
  };
